/**
 * SEO da home do blog (/blog) e das categorias (/blog/category/<slug>).
 *
 * Mesmo esquema do SeoPLP.tsx: o `loader` monta título/descrição/canonical
 * e um JSON-LD enxuto (Blog com os links dos posts + BreadcrumbList) e
 * descarta o `BlogPostListingPage` bruto, que traria o HTML inteiro de cada
 * post pro payload de hidratação.
 */
import type { BlogPostListingPage } from "@decocms/apps-blog/types";
import { PROD_URL_HTTPS } from "~/utils/constants";
import { postUrl } from "~/components/blog/utils";
import { markPageNotFound } from "~/server/pageStatus";

export interface Props {
  jsonLD: BlogPostListingPage | null;
  /** @title Título (home do blog) */
  title?: string;
  /** @title Descrição (home do blog) */
  description?: string;
}

export const loader = (props: Props, req?: Request) => {
  const page = props.jsonLD;
  const url = new URL(req?.url ?? `${PROD_URL_HTTPS}/blog`);
  const pageNumber = Number(url.searchParams.get("page") ?? 1);
  const category = page?.category;

  const baseTitle = category ? `${category.name} | Blog Daniela Tombini` : props.title;
  const title = pageNumber > 1 && baseTitle ? `${baseTitle} - Página ${pageNumber}` : baseTitle;
  const description = category
    ? category.description ||
      `Posts sobre ${category.name.toLowerCase()} no blog da Daniela Tombini: moda, conforto e estilo.`
    : props.description;

  // Categoria inexistente ou página além da última (busca sem resultado é
  // resposta válida).
  if (!page?.posts?.length && !url.searchParams.has("q")) markPageNotFound();

  const canonical = `${PROD_URL_HTTPS}${url.pathname}${pageNumber > 1 ? `?page=${pageNumber}` : ""}`;

  const crumbs = [
    { name: "Home", item: `${PROD_URL_HTTPS}/` },
    { name: "Blog", item: `${PROD_URL_HTTPS}/blog` },
    ...(category ? [{ name: category.name, item: `${PROD_URL_HTTPS}${url.pathname}` }] : []),
  ];

  return {
    title,
    description,
    canonical,
    // Listagem vazia (página além da última, categoria sem post) e resultado
    // de busca (?q=) não entram no índice.
    noIndexing: !page?.posts?.length || url.searchParams.has("q"),
    // Um único script com @graph: com dois scripts separados, o <head>
    // reconstruído no client depois da hidratação repetia o BreadcrumbList
    // no lugar do Blog (o HTML do servidor saía certo).
    jsonLDs: page?.posts?.length
      ? [{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Blog",
            name: title,
            url: canonical,
            blogPost: page.posts.map((post) => ({
              "@type": "BlogPosting",
              headline: post.title,
              url: `${PROD_URL_HTTPS}${postUrl(post.slug)}`,
              ...(post.date ? { datePublished: post.date } : {}),
            })),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: crumbs.map((crumb, index) => ({
              "@type": "ListItem",
              position: index + 1,
              ...crumb,
            })),
          },
        ],
      }]
      : [],
    jsonLD: null,
  };
};

export const eager = true;
export const sync = true;

// Tudo vai pro <head> via `jsonLDs` do loader; nada a renderizar no <body>.
export default function SeoBlogListing(_props: Props) {
  return null;
}
